import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { TextField, Box, Radio, Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useListing, useAlert } from 'hooks';
import {
  addVirtualServiceOrderAsync,
  hideModal,
  getVirtualServiceOrdersAsync,
  createInvoiceForAdditionalServiceAsync,
} from 'state/actions';

import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { ALERT, MODAL, SERVICEMODALSTEP } from 'config/constants';
import { HvModal, HvPleaseWait, HvButton } from 'components';
import { VirtualServiceOrder, VirtualServiceOrderItem } from 'models';
import { remove } from 'lodash';
import { InputChangeEvent } from 'types';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 550,
  },
  picture: {
    width: 200,
  },
  backButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  option: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  optionRow: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  warning: {
    fontSize: 10,
    color: '#FF4A4A',
  },
  items: {
    overflowY: 'scroll',
    maxHeight: 500,
  },
  code: {
    width: '100%',
    marginRight: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    alignItems: 'center',
  },
  itemRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    padding: 8,
  },
  deleteIcon: {
    alignSelf: 'center',
    marginRight: 5,
    cursor: 'pointer',
  },
});
interface Props {
  setActiveStep: (_: number) => void;
  virtualServiceOrder: VirtualServiceOrder;
  selectedService: any;
  setOrder: (virtualServiceOrder: VirtualServiceOrder) => void;
  setSelectedImages: (_: any) => void;
}
function BlueHourReview({
  setActiveStep,
  virtualServiceOrder,
  selectedService,
  setOrder,
  setSelectedImages,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();

  const { token } = useAuth();
  const { user } = useAuth();
  const { displayAlert } = useAlert();
  const path = window.location.pathname;
  const [load, setLoad] = useState(false);
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { addedVirtualServiceOrderStatus, listing } = useListing(listing_token);
  const items = virtualServiceOrder.virtualServiceOrderItems;

  const fmtNext = intl.formatMessage({ id: 'deliver.photos.next' });

  useEffect(() => {
    if (addedVirtualServiceOrderStatus) {
      setLoad(false);
      if (addedVirtualServiceOrderStatus === 200) {
        displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'deliver.photos.success' }));
      } else {
        displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'deliver.photos.error' }));
      }
      dispatch(hideModal(MODAL.VIRTUAL_STAGING));
      dispatch(getVirtualServiceOrdersAsync(token, listing.id, user.id));
    }
  }, [addedVirtualServiceOrderStatus]);
  const onBack = () => {
    setActiveStep(SERVICEMODALSTEP.OPTION);
  };

  const onClose = () => {
    dispatch(hideModal(MODAL.VIRTUAL_STAGING));
  };

  const onNext = () => {
    setActiveStep(SERVICEMODALSTEP.SUMMARY);
  };

  const onDelete = (item: VirtualServiceOrderItem) => {
    const newItems = remove(items, (i) => {
      return i.picture_id !== item.picture_id;
    });
    if (!newItems.length) {
      setActiveStep(1);
      setSelectedImages([]);
    }
    setOrder({
      id: null,
      listing_id: listing.id,
      virtualServiceOrderItems: newItems,
      user_id: user.id,
      created_at: new Date(),
      shooting_id: listing.validated_shooting_id,
      service_offer_id: selectedService.id,
    });
  };
  const onNoteChange = (e: InputChangeEvent, item: VirtualServiceOrderItem) => {
    const foundCurrentItem = items.find((i) => {
      return i.picture_id === item.picture_id;
    });
    if (foundCurrentItem) {
      foundCurrentItem.note = e.target.value;
      foundCurrentItem.isDelegated = true;
    }
    setOrder({
      id: null,
      listing_id: listing.id,
      virtualServiceOrderItems: items,
      user_id: user.id,
      created_at: new Date(),
      shooting_id: listing.validated_shooting_id,
      service_offer_id: selectedService.id,
    });
  };
  const validateOrder = () => {
    if (!virtualServiceOrder) {
      return true;
    }
    return false;
  };
  const buttons = (
    <Box>
      <HvButton className={styles.backButton} onClick={onBack}>
        {intl.formatMessage({ id: 'deliver.photos.back' })}
      </HvButton>
      <HvButton disabled={validateOrder()} onClick={onNext}>
        {fmtNext}
      </HvButton>
    </Box>
  );
  return (
    <HvModal
      title={intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.optionChoice' })}
      headerImage='virtualStagingBg'
      buttons={buttons}
      onClose={onClose}
    >
      <Box className={styles.content}>
        <Grid container className={styles.row}>
          <Grid item>
            <Typography>
              {`${items.length} ${intl.formatMessage({
                id: 'deliver.photos.virtualServiceModal.photos',
              })}`}
            </Typography>
          </Grid>
        </Grid>
        <Box className={styles.items}>
          {items.map((item, index) => {
            return (
              <Box key={index} className={styles.itemRow}>
                <Box
                  className={styles.row}
                  onClick={() => {
                    return onDelete(item);
                  }}
                >
                  <RemoveCircleIcon className={styles.deleteIcon} color='primary' />
                  <img className={styles.picture} src={item.picture_thumbnail_url} alt='item' />
                </Box>
                {item.isDelegated && (
                  <Box className={styles.optionRow}>
                    <TextField
                      className={styles.code}
                      label={intl.formatMessage({
                        id: 'deliver.photos.virtualServiceModal.note',
                      })}
                      variant='outlined'
                      multiline
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        return onNoteChange(event, item);
                      }}
                      value={item.note ? item.note : ''}
                      minRows={4}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      {load ? <HvPleaseWait /> : null}
    </HvModal>
  );
}
export default BlueHourReview;
